// Информация об организации

import utils from "../utils";

const founderShareFormatters = {
    PERCENT(share) {
        return `${utils.round(share.value, 2)}%`;
    },
    DECIMAL(share) {
        return `${utils.round(share.value * 100, 2)}%`;
    },
    FRACTION(share) {
        return `${utils.round((share.numerator / share.denominator) * 100, 2)}%`;
    },
};

function capitalize(str) {
    if (!str) {
        return "";
    }
    return str[0].toUpperCase() + str.substring(1);
}

export default {
    address(party) {
        const { address } = party;
        if (address.data && address.data.qc === "0" && address.data.postal_code) {
            return utils.join([address.data.postal_code, address.value]);
        }
        return address.value;
    },

    authority(party, name) {
        const { authorities } = party;
        if (!authorities || !authorities[name]) {
            return "";
        }
        const authority = authorities[name];
        return `${authority.name} (код ${authority.code})`;
    },

    capital(party) {
        if (!party.capital) {
            return "";
        }
        return `${utils.formatMoney(party.capital.value)}`;
    },

    egrulLink(party) {
        if (!party.inn) {
            return "";
        }
        return (
            `https://pb.nalog.ru/search.html#quick-result?queryAll=${
                party.inn
            }&mode=search-all&page=1&pageSize=10`
        );
    },

    emails(party) {
        if (!party.emails) {
            return "";
        }
        return party.emails.filter(email => email.data != null);
    },

    employeeCount(party) {
        if (!party.employee_count) {
            return "";
        }
        const plural = utils.pluralize(party.employee_count, [
            "coтрудник",
            "сотрудника",
            "сотрудников",
        ]);
        return `${party.employee_count} ${plural}`;
    },

    financeField(party, fieldName) {
        if (!party.finance) {
            return "—";
        }
        const value = party.finance[fieldName];
        if (!value) {
            return "—";
        }
        return `${utils.formatMoney(value)}`;
    },

    founderName(founder) {
        if (!founder) {
            return "";
        }
        if (founder.fio) {
            return utils.join([founder.fio.surname, founder.fio.name, founder.fio.patronymic], " ");
        }
        return founder.name;
    },

    founderShare(share) {
        if (!share) {
            return "";
        }
        const formatter = founderShareFormatters[share.type];
        return formatter(share);
    },

    fts(party) {
        if (!party.authorities || !party.authorities.fts_report) {
            return "";
        }
        const authority = party.authorities.fts_report;
        let fts = `${authority.name} (код ${authority.code})`;
        if (party.documents && party.documents.fts_report) {
            const reportDate = utils.formatDate(party.documents.fts_report.issue_date);
            fts += ` с ${reportDate}`;
        }
        return fts;
    },

    fullname(party) {
        if (!party.name) {
            return "";
        }
        return party.name.full_with_opf || "";
    },

    geo(party) {
        const address = party.address.data;
        if (!address || !address.qc_geo) {
            return "";
        }
        return `${address.geo_lat},${address.geo_lon}`;
    },

    hasAccurateGeo(party) {
        const address = party.address.data;
        return address && address.qc_geo && (address.qc_geo === "0" || address.qc_geo === "1");
    },

    hasManager(party) {
        const hasManagers = Boolean(party.managers);
        const manager = (party.managers && party.managers[0]) || {};
        const hasFio = manager.hasOwnProperty("fio");
        const hasManagement = Boolean(party.management);
        return hasManagers && (hasFio || hasManagement);
    },

    isIndividual(party) {
        return party.type === "INDIVIDUAL";
    },

    isLegal(party) {
        return party.type === "LEGAL";
    },

    managerInn(party) {
        if (!party.managers) {
            return "";
        }
        const manager = party.managers[0];
        let inn = "";
        if (manager.inn) {
            inn = `ИНН ${manager.inn}`;
        }
        return inn;
    },

    managerName(party) {
        if (!party.managers) {
            return "";
        }
        const manager = party.managers[0];
        if (manager.fio) {
            return utils.join([manager.fio.surname, manager.fio.name, manager.fio.patronymic], " ");
        }
        return party.management.name;
    },

    managerTitle(party) {
        if (!party.managers) {
            return "";
        }
        const post = party.managers[0].post || "Руководитель";
        return capitalize(post);
    },

    name(party) {
        if (!party.name) {
            return "";
        }
        return party.name.short_with_opf || party.name.full_with_opf;
    },

    okved(party) {
        if (!party.okveds) {
            return "";
        }
        const okved = party.okveds[0];
        return `${okved.name} (${okved.code})`;
    },

    phones(party) {
        if (!party.phones) {
            return "";
        }
        return party.phones.filter(phone => phone.data != null);
    },

    registration(party) {
        if (!party.documents || !party.documents.fts_registration) {
            return "";
        }
        const doc = party.documents.fts_registration;
        let ret = utils.join(
            ["№", doc.series, doc.number, "от", utils.formatDate(doc.issue_date)],
            " ",
        );
        ret += `, ИФНС ${doc.issue_authority}`;
        return ret;
    },

    registrationDate(party) {
        if (!party.state || !party.state.registration_date) {
            return "";
        }
        return utils.formatDate(party.state.registration_date);
    },

    reportDate(party) {
        if (!party.documents || !party.documents.fts_report) {
            return "";
        }
        return utils.formatDate(party.documents.fts_report.issue_date);
    },

    smb(party) {
        if (!party.documents || !party.documents.smb) {
            return "—";
        }
        const { smb } = party.documents;
        const categories = {
            MICRO: "Микропредприятие",
            SMALL: "Малое предприятие",
            MEDIUM: "Среднее предприятие",
        };
        return `${categories[smb.category]} с ${utils.formatDate(smb.issue_date)}`;
    },

    state(party) {
        const { state } = party;
        const legalType = party.type;
        if (!state || !state.status) {
            return "";
        }
        if (state.status === "ACTIVE") {
            return legalType === "LEGAL" ? "Действующая организация" : "Действующий ИП";
        }
        if (state.status === "LIQUIDATING") {
            return "Ликвидируется";
        }
        if (state.status === "REORGANIZING") {
            return "Присоединяется к другой организации";
        }
        if (state.status === "BANKRUPT") {
            return "Банкротство";
        }
        return utils.join(["Ликвидирована", utils.formatDate(state.liquidation_date)], " ");
    },

    taxSystem(party) {
        if (!party.finance) {
            return "";
        }
        const taxSystem = party.finance.tax_system;
        const taxSystems = {
            ESHN: "ЕСХН",
            ENVD: "ЕНВД",
            ENVD_ESHN: "ЕНВД + ЕСХН",
            SRP: "СРП",
            USN: "УСН",
            USN_ENVD: "УСН + ЕНВД",
        };
        return taxSystems[taxSystem] || "—";
    },
};
